import { Button, Card, Empty, Input, message, Modal, Radio, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import request from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { Dialog as ZhDialog } from "zhihao-ui-react";
import { OIL_ORDER_STATE, OIL_TYPE, TIME_PERIOD } from "@zhihaoscm/meta/src/oilOrder";
import { OIL_SITE_TYPE } from "@zhihaoscm/meta/src/oilSite";
import { loadOilOrderDetail } from "@/views/pages/logistics/oil/OilOrderDetailPage";
import { loadPlatformShipNeedDetail } from "@/views/pages/logistics/shippingNeeds/PlatformShipNeedDetailPage";
import type { FeaturesFeedbackCustomer, FeaturesFeedbackRow } from "./FeaturesFeedbackPage";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

interface FeaturesFeedbackHandlerRecord {
    handlerName?: string;
    remark?: string;
    handleTime?: string;
}

export interface FeaturesFeedbackDetail extends FeaturesFeedbackRow {
    feedback?: number[];
    additionalNotes?: string;
    snapshot?: string;
    handlerRecord?: FeaturesFeedbackHandlerRecord[];
}

export interface FeaturesFeedbackDetailProps {
    feedbackId?: number | string;
    loadFeedback?: (id: number | string) => Promise<FeaturesFeedbackDetail | undefined>;
    processFeedback?: (id: number | string, form: { state: number; remark: string }) => Promise<void>;
    loadRelatedBusiness?: (type: number, srpId: string) => Promise<unknown>;
    navigate?: (to: { name: string; params: { id: string } } | number) => void;
    permissions?: string[];
}

const feedbackTypeLabels: Record<number, string> = {
    1: "货源反馈",
    2: "船主取消报价",
    3: "货主取消找船需求",
    4: "船主取消油品订单"
};
const feedbackProblemLabels: Record<number, string> = {
    1: "时间不合适",
    6: "误操作,看错时间、路线"
};
const processStateLabels: Record<number, string> = { 1: "待处理", 2: "处理中", 3: "已处理" };
const snapshotLabels: Record<number, string> = {
    1: "船运需求快照",
    2: "船运需求快照",
    3: "找船需求快照",
    4: "油品订单快照"
};
const snapshotDialogTitles: Record<number, string> = {
    1: "船运需求详情",
    2: "报价详情",
    3: "我的找船需求详情",
    4: "订单详情"
};
const snapshotProcessLabels: Record<number, string> = { 1: "待处理", 2: "处理中", 3: "已处理" };

type SnapshotRecord = Record<string, unknown>;

/** 将快照字段转成 Vue 页面一致的缺省展示值。 */
const snapshotText = (value: unknown) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

/** 将油品快照中的枚举编码转换为 Vue 页面使用的元数据文案。 */
const snapshotMetaText = (getIntroByCode: (code: number | string | undefined) => string | null, value: unknown) => {
    if (value === undefined || value === null || value === "") return DEFAULT_VALUE;
    const numericCode = Number(value);
    const code = Number.isNaN(numericCode) ? String(value) : numericCode;
    return getIntroByCode(code) || snapshotText(value);
};

/** 读取 JSON 快照对象；结构不完整时保留原始内容兜底。 */
const parseSnapshot = (snapshot?: string): SnapshotRecord | undefined => {
    if (!snapshot) return undefined;
    try {
        const parsed: unknown = JSON.parse(snapshot);
        return typeof parsed === "object" && parsed !== null ? (parsed as SnapshotRecord) : undefined;
    } catch {
        return undefined;
    }
};

/** 按 Vue 快照组件结构化渲染四类快照，其余结构继续展示原始内容。 */
const renderSnapshotContent = (type: number, snapshot?: string) => {
    const parsed = parseSnapshot(snapshot);
    const plat = parsed?.plat && typeof parsed.plat === "object" ? (parsed.plat as SnapshotRecord) : undefined;
    const quote = parsed?.accept && typeof parsed.accept === "object" ? (parsed.accept as SnapshotRecord) : undefined;
    const platVo = parsed?.platVo && typeof parsed.platVo === "object" ? (parsed.platVo as SnapshotRecord) : undefined;
    const quotePlat = platVo?.plat && typeof platVo.plat === "object" ? (platVo.plat as SnapshotRecord) : undefined;
    if (type === 2 && quote && quotePlat) {
        const shipInfo =
            Array.isArray(quote.shipInfos) && typeof quote.shipInfos[0] === "object"
                ? (quote.shipInfos[0] as SnapshotRecord)
                : undefined;
        const quoteUnitPrice =
            typeof shipInfo?.unitPrice === "number" ? shipInfo.unitPrice : Number(shipInfo?.unitPrice || 0);
        const quoteTon = typeof shipInfo?.ton === "number" ? shipInfo.ton : Number(shipInfo?.ton || 0);
        return (
            <Space direction="vertical" className="w-full">
                <Typography.Title level={5}>报价信息</Typography.Title>
                <ZhGrid cols={1} gap={[20, 12]}>
                    <ZhBaseItem label="抢单时间" value={formatDate(snapshotText(quote.createdTime))} />
                    <ZhBaseItem
                        label="抢单状态"
                        value={snapshotProcessLabels[Number(quote.state)] || snapshotText(quote.state)}
                    />
                    <ZhBaseItem label="船舶名称" value={snapshotText(shipInfo?.cnname || shipInfo?.name)} />
                    <ZhBaseItem label="报价单价" value={`${quoteUnitPrice.toFixed(2)}元/吨`} />
                    <ZhBaseItem label="装载吨数" value={snapshotText(quoteTon)} />
                    <ZhBaseItem label="预估运费" value={`${(quoteUnitPrice * quoteTon).toFixed(2)}元`} />
                    <ZhBaseItem
                        label="银行账号"
                        value={snapshotText((quote.captainBankInfo as SnapshotRecord | undefined)?.account)}
                    />
                    <ZhBaseItem
                        label="开户名称"
                        value={snapshotText((quote.captainBankInfo as SnapshotRecord | undefined)?.name)}
                    />
                    <ZhBaseItem
                        label="开户行"
                        value={snapshotText((quote.captainBankInfo as SnapshotRecord | undefined)?.bank)}
                    />
                    <ZhBaseItem label="补充约定" value={snapshotText(quote.replenishAppoint)} />
                    <ZhBaseItem label="拒绝原因" value={snapshotText(quote.rejectReason)} />
                </ZhGrid>
                <Typography.Title level={5}>船运需求信息</Typography.Title>
                <ZhGrid cols={1} gap={[20, 12]}>
                    <ZhBaseItem label="编号" value={snapshotText(quotePlat.id)} />
                    <ZhBaseItem label="始发地" value={snapshotText(quotePlat.sourcePortName)} />
                    <ZhBaseItem label="目的地" value={snapshotText(quotePlat.destinationPortName)} />
                    <ZhBaseItem label="货物类型" value={snapshotText(quotePlat.goodsType)} />
                </ZhGrid>
            </Space>
        );
    }
    const shipping =
        parsed?.shipping && typeof parsed.shipping === "object" ? (parsed.shipping as SnapshotRecord) : undefined;
    if (type === 3 && shipping) {
        const unitPrice = Number(shipping.unitPrice || 0);
        const freightTons = Number(shipping.freightTons || 0);
        const loadDate = Array.isArray(shipping.loadDate)
            ? shipping.loadDate.join("-")
            : snapshotText(shipping.loadDate);
        return (
            <Space direction="vertical" className="w-full">
                <ZhGrid cols={1} gap={[20, 12]}>
                    <ZhBaseItem label="货物类型" value={snapshotText(shipping.goodsType)} />
                    <ZhBaseItem label="运单状态" value={snapshotText(shipping.state)} />
                    <ZhBaseItem label="始发地" value={snapshotText(shipping.sourcePortName)} />
                    <ZhBaseItem label="目的地" value={snapshotText(shipping.destinationPortName)} />
                    <ZhBaseItem label="期望装载日期" value={`${loadDate} +${snapshotText(shipping.loadDays)}天`} />
                    <ZhBaseItem label="卸载天数" value={`${snapshotText(shipping.loadUnloadDays)}天`} />
                    <ZhBaseItem label="运货吨数" value={snapshotText(shipping.freightTons)} />
                    <ZhBaseItem label="意向运价" value={`￥${unitPrice.toFixed(2)} 元/吨`} />
                </ZhGrid>
                <Typography.Title level={5}>找船需求信息</Typography.Title>
                <Typography.Text strong>船舶吨位要求</Typography.Text>
                <ZhGrid cols={1} gap={[20, 12]}>
                    <ZhBaseItem label="最小吨位" value={snapshotText(shipping.tonMin)} />
                    <ZhBaseItem label="最大吨位" value={snapshotText(shipping.tonMax)} />
                    <ZhBaseItem label="预估运费" value={`￥${(unitPrice * freightTons).toFixed(2)}`} />
                    <ZhBaseItem
                        label="可接受滞期费"
                        value={`￥${Number(shipping.demurrageFee || 0).toFixed(2)} 元/吨/天`}
                    />
                    <ZhBaseItem label="船运定金" value={`￥${Number(shipping.deposit || 0).toFixed(2)}元`} />
                    <ZhBaseItem label="联系人" value={snapshotText(shipping.contact)} />
                    <ZhBaseItem label="手机号" value={snapshotText(shipping.mobile)} />
                    <ZhBaseItem label="补充约定" value={snapshotText(shipping.replenishAppoint)} />
                </ZhGrid>
            </Space>
        );
    }
    const oilOrder =
        parsed?.oilOrder && typeof parsed.oilOrder === "object" ? (parsed.oilOrder as SnapshotRecord) : undefined;
    const oilSite =
        parsed?.oilSite && typeof parsed.oilSite === "object" ? (parsed.oilSite as SnapshotRecord) : undefined;
    if (type === 4 && oilOrder && oilSite) {
        return (
            <Space direction="vertical" className="w-full">
                <ZhGrid cols={1} gap={[20, 12]}>
                    <ZhBaseItem label="编号" value={snapshotText(oilOrder.id)} />
                    <ZhBaseItem
                        label="状态"
                        value={snapshotMetaText(
                            OIL_ORDER_STATE.getIntroByCode.bind(OIL_ORDER_STATE),
                            oilOrder.beforeCancelState
                        )}
                    />
                    <ZhBaseItem label="加油站">
                        <span>{snapshotText(oilSite.name)}</span>
                        <span>
                            ({snapshotMetaText(OIL_SITE_TYPE.getIntroByCode.bind(OIL_SITE_TYPE), oilSite.type)})
                        </span>
                    </ZhBaseItem>
                    <ZhBaseItem label="地址" value={snapshotText(oilSite.address)} />
                </ZhGrid>
                <Typography.Title level={5}>预约信息</Typography.Title>
                <ZhGrid cols={1} gap={[20, 12]}>
                    <ZhBaseItem
                        label="预约船舶"
                        value={`${snapshotText(oilOrder.shipCnName)}/${snapshotText(oilOrder.shipName)}`}
                    />
                    <ZhBaseItem label="MMSI号" value={snapshotText(oilOrder.shipId)} />
                    <ZhBaseItem label="联系人" value={snapshotText(oilOrder.captainName)} />
                    <ZhBaseItem label="联系方式" value={snapshotText(oilOrder.captainMobile)} />
                    <ZhBaseItem
                        label="油品类型"
                        value={snapshotMetaText(OIL_TYPE.getIntroByCode.bind(OIL_TYPE), oilOrder.oilType)}
                    />
                    <ZhBaseItem label="预估加油量" value={`${snapshotText(oilOrder.bookingRefuelingVolume)}吨`} />
                    <ZhBaseItem label="预估油价" value={`${snapshotText(oilOrder.bookingRefuelingPrice)}/吨`} />
                    <ZhBaseItem label="预估油费" value={snapshotText(oilOrder.bookingRefuelingCost)} />
                    <ZhBaseItem
                        label="预约加油时间"
                        value={`${snapshotText(oilOrder.bookingRefuelingTime)}${snapshotMetaText(TIME_PERIOD.getIntroByCode.bind(TIME_PERIOD), oilOrder.bookingRefuelingTimePeriod)}`}
                    />
                    <ZhBaseItem label="提交预约时间" value={formatDate(snapshotText(oilOrder.createdTime))} />
                    <ZhBaseItem label="备注" value={snapshotText(oilOrder.remark)} />
                </ZhGrid>
            </Space>
        );
    }
    if (type !== 1 || !plat) {
        return <pre className="whitespace-pre-wrap break-all">{snapshot || DEFAULT_VALUE}</pre>;
    }

    const unitPrice =
        typeof plat.unitPrice === "number" ? `${plat.unitPrice.toFixed(2)} 元/吨` : snapshotText(plat.unitPrice);
    return (
        <Space direction="vertical" className="w-full">
            <div className="flex w-full items-center justify-between">
                <Typography.Text strong>始发地</Typography.Text>
                <Typography.Text>{snapshotText(plat.sourcePortName)}</Typography.Text>
                <Typography.Text strong>目的地</Typography.Text>
                <Typography.Text>{snapshotText(plat.destinationPortName)}</Typography.Text>
            </div>
            <Typography.Title level={5}>船运需求信息</Typography.Title>
            <ZhGrid cols={1} gap={[20, 12]}>
                <ZhBaseItem label="编号" value={snapshotText(plat.id)} />
                <ZhBaseItem label="货物类型" value={snapshotText(plat.goodsType)} />
                <ZhBaseItem label="运货吨数" value={snapshotText(plat.freightTons)} />
                <ZhBaseItem label="意向运价" value={unitPrice} />
                <ZhBaseItem label="货主补充约定" value={snapshotText(plat.replenishAppoint)} />
            </ZhGrid>
        </Space>
    );
};

/** 复用 Vue 反馈详情接口。 */
export async function loadFeaturesFeedbackDetail(id: number | string) {
    const response = await request.service.get(`/features-feedback/find/${id}`);
    return response.data as FeaturesFeedbackDetail | undefined;
}

/** 复用 Vue 反馈处理接口。 */
export async function processFeaturesFeedback(id: number | string, form: { state: number; remark: string }) {
    await request.service.put(`/features-feedback/handle/${id}`, form);
}

const formatCustomer = (customer?: FeaturesFeedbackCustomer) =>
    customer
        ? `${customer.realName || DEFAULT_VALUE} | ${customer.mobile || DEFAULT_VALUE} | ${customer.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 用户反馈详情页，保留 Vue 的基本信息、问题、处理记录和处理弹窗。 */
export function FeaturesFeedbackDetailPage({
    feedbackId,
    loadFeedback = loadFeaturesFeedbackDetail,
    processFeedback = processFeaturesFeedback,
    loadRelatedBusiness = async (type, srpId) =>
        type === 4 ? loadOilOrderDetail(srpId) : loadPlatformShipNeedDetail(srpId),
    navigate: navigateOverride,
    permissions
}: FeaturesFeedbackDetailProps) {
    const routeParams = useParams<{ id: string }>();
    const routerNavigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 保持 Vue v-auth 语义：反馈处理写权限独立控制详情处理入口。
    const canProcess = (permissions ?? storePermissions).includes("ROLE_FUNCTION_W");
    const resolvedFeedbackId = feedbackId ?? routeParams.id ?? 0;
    const [detail, setDetail] = useState<FeaturesFeedbackDetail>();
    const [processOpen, setProcessOpen] = useState(false);
    const [processState, setProcessState] = useState(2);
    const [remark, setRemark] = useState("");
    const [remarkError, setRemarkError] = useState("");
    const [error, setError] = useState("");
    const [processError, setProcessError] = useState("");
    const [snapshotOpen, setSnapshotOpen] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadFeedback(resolvedFeedbackId)
            .then(result => {
                if (!disposed) {
                    setDetail(result);
                    setError("");
                }
            })
            .catch(() => {
                if (!disposed) setError("功能反馈详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadFeedback, resolvedFeedbackId]);

    /** 返回列表使用 React Router 的历史导航；关联业务保留可注入的 Vue 命名路由契约。 */
    const navigateBack = () => routerNavigate(-1);

    /** 校验 Vue 处理弹窗的必填备注并提交状态与备注。 */
    const submitProcess = async () => {
        if (!remark.trim()) {
            setRemarkError("请输入备注");
            return;
        }
        try {
            await processFeedback(resolvedFeedbackId, { state: processState, remark: remark.trim() });
            notifyOperationSuccess("处理成功");
            setDetail(current => (current ? { ...current, state: processState } : current));
            setProcessOpen(false);
            setRemarkError("");
            setProcessError("");
        } catch {
            setProcessError("功能反馈处理失败");
        }
    };

    if (!detail) {
        return error ? (
            <section aria-label="features-feedback-detail-page">
                <Card>
                    <Alert role="alert" type="error" message={error} />
                </Card>
            </section>
        ) : (
            <Empty description="暂无数据" />
        );
    }

    /** 按 Vue 反馈类型映射快照入口文案和弹窗标题。 */
    const snapshotLabel = snapshotLabels[Number(detail.type)] || "快照";
    const snapshotDialogTitle = snapshotDialogTitles[Number(detail.type)] || "快照详情";

    /** 按 Vue 详情页契约校验关联业务存在后再跳转。 */
    const openRelatedBusiness = async () => {
        if (!detail.srpId) return;
        try {
            await loadRelatedBusiness(Number(detail.type), String(detail.srpId));
            const relatedId = encodeURIComponent(String(detail.srpId));
            if (navigateOverride) {
                navigateOverride(
                    Number(detail.type) === 4
                        ? { name: "OilTypeManagementDetail", params: { id: String(detail.srpId) } }
                        : { name: "PlatformShipNeedInfo", params: { id: String(detail.srpId) } }
                );
            } else {
                routerNavigate(
                    Number(detail.type) === 4
                        ? `/logistics/oil/oilTypeManagement/detail/${relatedId}`
                        : `/logistics/shippingNeed/platformShipNeedInfo/${relatedId}`
                );
            }
        } catch {
            message.error("该页面不存在");
        }
    };

    return (
        <section aria-label="features-feedback-detail-page" className="flex min-h-full flex-col gap-4">
            <div className="section-card">
                <Space direction="vertical" size="large" className="w-full">
                    <ZhDetailHeader
                        rightInfos={[{ label: "反馈时间", value: formatDate(detail.createdTime) }]}
                        stateDesc={processStateLabels[Number(detail.state)] || DEFAULT_VALUE}
                        title="反馈详情"
                    />
                    <div className="section-title">基本信息</div>
                    <ZhGrid cols={4} gap={[50, 20]}>
                        <ZhBaseItem
                            label={Number(detail.type) === 3 ? "账号" : "承运商"}
                            value={formatCustomer(detail.customerEnterprise)}
                        />
                        <ZhBaseItem label="反馈类型" value={feedbackTypeLabels[Number(detail.type)] || DEFAULT_VALUE} />
                        <ZhBaseItem label="关联业务编号">
                            {detail.srpId ? (
                                <Button
                                    type="link"
                                    aria-label={`查看关联业务 ${detail.srpId}`}
                                    onClick={() => void openRelatedBusiness()}
                                >
                                    {detail.srpId}
                                </Button>
                            ) : (
                                DEFAULT_VALUE
                            )}
                        </ZhBaseItem>
                        <ZhBaseItem label="联系方式" value={detail.mobile || DEFAULT_VALUE} />
                        <ZhBaseItem label="反馈问题" span={4}>
                            {detail.feedback?.length ? (
                                <Space>
                                    {detail.feedback.map(value => (
                                        <span key={value}>{feedbackProblemLabels[value] || DEFAULT_VALUE}</span>
                                    ))}
                                </Space>
                            ) : (
                                DEFAULT_VALUE
                            )}
                        </ZhBaseItem>
                        <ZhBaseItem label="补充说明" span={4} value={detail.additionalNotes || DEFAULT_VALUE} />
                        <ZhBaseItem label="快照" span={4}>
                            <Button type="link" aria-label="查看快照" onClick={() => setSnapshotOpen(true)}>
                                {snapshotLabel}
                            </Button>
                        </ZhBaseItem>
                    </ZhGrid>
                    <div className="section-title">处理记录</div>
                    {detail.handlerRecord?.length ? (
                        <Space direction="vertical">
                            {detail.handlerRecord.map((record, index) => (
                                <Typography.Text key={`${record.handleTime || "record"}-${index}`}>
                                    {`${index + 1}. ${formatDate(record.handleTime)}，处理人 ${record.handlerName || DEFAULT_VALUE}，备注 ${record.remark || DEFAULT_VALUE}`}
                                </Typography.Text>
                            ))}
                        </Space>
                    ) : (
                        <Typography.Text type="secondary">暂无处理记录</Typography.Text>
                    )}
                    <Space>
                        {canProcess && Number(detail.state) !== 3 ? (
                            <Button
                                type="primary"
                                aria-label="处理反馈"
                                disabled={banButton}
                                onClick={() => {
                                    setProcessError("");
                                    setProcessOpen(true);
                                }}
                            >
                                处理
                            </Button>
                        ) : null}
                        <Button aria-label="返回反馈列表" onClick={navigateBack}>
                            返回
                        </Button>
                    </Space>
                </Space>
            </div>
            <ZhDialog
                title={snapshotDialogTitle}
                open={snapshotOpen}
                width={360}
                footer={null}
                onCancel={() => setSnapshotOpen(false)}
            >
                {renderSnapshotContent(Number(detail.type), detail.snapshot)}
            </ZhDialog>
            <Modal
                title="处理"
                open={processOpen}
                onCancel={() => setProcessOpen(false)}
                onOk={() => void submitProcess()}
                okText="确认"
                cancelText="取消"
            >
                <Space direction="vertical" className="w-full">
                    {processError ? <Alert role="alert" type="error" message={processError} /> : null}
                    <Typography.Text>是否处理</Typography.Text>
                    <Radio.Group
                        aria-label="处理状态"
                        value={processState}
                        onChange={event => setProcessState(event.target.value)}
                    >
                        <Radio value={2}>处理中</Radio>
                        <Radio value={3}>已处理</Radio>
                    </Radio.Group>
                    <Input.TextArea
                        aria-label="备注"
                        maxLength={200}
                        rows={3}
                        placeholder="请输入备注"
                        value={remark}
                        onChange={event => {
                            setRemark(event.target.value);
                            setRemarkError("");
                        }}
                    />
                    {remarkError ? <Typography.Text type="danger">{remarkError}</Typography.Text> : null}
                </Space>
            </Modal>
        </section>
    );
}
